import React, { useEffect, useState, useMemo, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import { CustomPaging, DataTypeProvider, PagingState, SortingState, SelectionState } from '@devexpress/dx-react-grid'
import {
    Grid,
    Table,
    TableHeaderRow,
    PagingPanel,
    TableSelection,
    TableColumnResizing,
    TableColumnVisibility,
} from '@devexpress/dx-react-grid-material-ui'
import { useSnackbar } from 'notistack'
import IconButton from '@mui/material/IconButton'
import DeleteIcon from '@mui/icons-material/Delete'
import EditIcon from '@mui/icons-material/Edit'
import LocalHospitalOutlinedIcon from '@mui/icons-material/LocalHospitalOutlined'
import WarningAmberIcon from '@mui/icons-material/WarningAmber'
import WarningIcon from '@mui/icons-material/Warning'
import ReportProblemIcon from '@mui/icons-material/ReportProblem'
import Link from '@mui/material/Link'
import { Paper, Tooltip, Box, useMediaQuery } from '@mui/material'
import { useJumboDialog } from '@jumbo/components/JumboDialog/hooks/useJumboDialog'
import { deleteMember, getMemberById } from 'app/redux/store/features/members'
import GenderTypeService from 'app/pages/registrations/customers/components/GenderTypeService'
import { formatDateTime } from 'app/services/utils/components/FormatDateTime/FormatDateTime'
import { formattedDate } from 'app/services/utils/components/FormatDateTime/FormatDateTime'
import NoDataCellWithLoading from 'app/services/utils/components/NoDataCellWithLoading'
import LoadingCircle from 'app/services/utils/components/LoadingCircle/LoadingCircle'
import { useJumboTheme } from '@jumbo/hooks'

/** @param {number | undefined | null} sev */
function medicalSeverityLabel(sev) {
    if (sev == null || sev === 0) return 'Normal'
    if (sev === 1) return 'Medium'
    if (sev === 2) return 'High'
    if (sev === 3) return 'Critical'
    return String(sev)
}

/**
 * @param {{ medical?: { medicalSeverity?: number; medicalEmergencyContactName?: string; medicalEmergencyContactPhone?: string; conditions?: { medicalConditionTypeName?: string }[] } }} row
 */
function memberMedicalSummary(row) {
    const med = row?.medical
    if (!med) return { show: false, title: '', Icon: null, color: undefined }
    const sev = typeof med.medicalSeverity === 'number' ? med.medicalSeverity : 0
    const names = (med.conditions ?? [])
        .map((c) => c.medicalConditionTypeName)
        .filter(Boolean)
    const hasConditions = names.length > 0
    const elevated = sev > 0
    const emName = (med.medicalEmergencyContactName ?? '').trim()
    const emPhone = (med.medicalEmergencyContactPhone ?? '').trim()
    const hasEmergencyLine = !!(emName || emPhone)
    if (!hasConditions && !elevated && !hasEmergencyLine) return { show: false, title: '', Icon: null, color: undefined }

    const condText = hasConditions ? names.join(', ') : 'No condition types recorded'
    let title = ''
    if (sev === 3 || sev === 2 || sev === 1) {
        title = `Medical alert: ${medicalSeverityLabel(sev)}${hasConditions ? ` — ${condText}` : ''}`
    } else if (hasConditions) {
        title = `Medical — ${condText}`
    }
    if (hasEmergencyLine) {
        const emLine = `Emergency: ${[emName, emPhone].filter(Boolean).join(' • ')}`
        title = title ? `${title}\n${emLine}` : emLine
    }

    if (sev === 3) return { show: true, title, Icon: ReportProblemIcon, color: 'error.main' }
    if (sev === 2) return { show: true, title, Icon: WarningIcon, color: 'warning.dark' }
    if (sev === 1) return { show: true, title, Icon: WarningAmberIcon, color: 'warning.main' }
    return { show: true, title, Icon: LocalHospitalOutlinedIcon, color: 'info.main' }
}

const MemberGridData = ({
    onPageChange,
    onColumnSort,
    setCurrentPage,
    currentPage,
    apiCallInProgress,
    setApiCallInProgress,
}) => {
    const navigate = useNavigate()
    const dispatch = useDispatch()
    const { showDialog, hideDialog } = useJumboDialog()
    const { enqueueSnackbar } = useSnackbar()
    const dateTimeColumns = ['updateTime']
    const dateColumns = ['birthDate']
    const [sorting, setSorting] = useState([{ columnName: 'firstName', direction: 'asc' }])
    const pageSize = 25
    const { members, loading: memberLoading, error: memberError, refreshing } = useSelector((state) => state.members)
    const { familyConfig, loading: familyLoading, error: familyError } = useSelector((state) => state.families)
    const { theme } = useJumboTheme()
    const sm = useMediaQuery(theme.breakpoints.down('sm'))
    const mddown = useMediaQuery(theme.breakpoints.down('md'))
    const md = useMediaQuery(theme.breakpoints.between('md', 'xl'))
    const min = sm || mddown || md

    useEffect(() => {
        if (!apiCallInProgress || memberLoading || familyLoading) return
        setApiCallInProgress(false)
        const error = memberError || familyError
        if (error) handleError(error)
    }, [memberLoading || familyLoading])

    function handleError(error) {
        if (error.status === '401') {
            navigate('/profile/signout')
        } else if (error.detail) {
            enqueueSnackbar(error.detail, {
                variant: 'error',
                anchorOrigin: {
                    vertical: 'top',
                    horizontal: 'right',
                },
            })
        } else {
            enqueueSnackbar(error, {
                variant: 'error',
                anchorOrigin: {
                    vertical: 'top',
                    horizontal: 'right',
                },
            })
        }
    }

    const onCurrentPageChange = (pageNumber) => {
        onPageChange(pageNumber)
        setCurrentPage(pageNumber)
    }

    const onSortingChange = (sortOrder) => {
        onColumnSort(sortOrder)
        setSorting(sortOrder)
    }

    const onNavigatingToDetail = useCallback(
        (id) => {
            dispatch(getMemberById({ familyId: familyConfig.familyId, id: id }))
            navigate(`/registrations/families/${familyConfig.familyId}/member/edit/${id}`, {
                state: { familyId: familyConfig.familyId, memberId: id },
            })
        },
        [dispatch, navigate, familyConfig.familyId]
    )

    const TableRow = ({ tableRow, ...restProps }) => {
        return (
            <TableSelection.Row
                {...restProps}
                onDoubleClick={() => {
                    onNavigatingToDetail(tableRow.row.id)
                }}
            />
        )
    }

    const NoDataCellWithLoadingType = (props) => <NoDataCellWithLoading loading={memberLoading} {...props} />

    const DateTimeTypeProvider = (props) => <DataTypeProvider formatterComponent={formatDateTime} {...props} />

    const DateTypeProvider = (props) => <DataTypeProvider formatterComponent={formattedDate} {...props} />

    const renderActionCell = (row) => {
        return (
            <div>
                <IconButton onClick={() => onNavigatingToDetail(row.id)}>
                    <EditIcon title='Edit row' sx={{ color: 'primary.main', fontSize: '18px' }} />
                </IconButton>
                <IconButton onClick={() => onRemoveUser(row.id)}>
                    <DeleteIcon sx={{ color: 'primary.main', fontSize: '18px' }} />
                </IconButton>
            </div>
        )
    }

    const renderLinkCell = (row) => {
        const { show, title, Icon, color } = memberMedicalSummary(row)
        return (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, minWidth: 0 }}>
                {show && Icon ? (
                    <Tooltip title={title} arrow>
                        <Box component='span' sx={{ display: 'inline-flex', flexShrink: 0, lineHeight: 0 }}>
                            <Icon sx={{ fontSize: 18, color }} aria-hidden />
                        </Box>
                    </Tooltip>
                ) : null}
                <Link
                    variant='text'
                    underline='none'
                    onClick={() => onNavigatingToDetail(row.id)}
                    sx={{
                        minWidth: 0,
                        '&:hover': {
                            color: 'action.active',
                            cursor: 'pointer',
                        },
                    }}>
                    {row.firstName}
                </Link>
            </Box>
        )
    }

    const hideDialogAndRefreshUserList = () => {
        hideDialog()
        if (memberError) {
            enqueueSnackbar(memberError.detail, {
                variant: 'error',
                anchorOrigin: {
                    vertical: 'top',
                    horizontal: 'right',
                },
            })
        } else {
            enqueueSnackbar('Member data removed successfully', {
                variant: 'success',
                anchorOrigin: {
                    vertical: 'top',
                    horizontal: 'right',
                },
            })
        }
    }

    const onRemoveUser = async (id) => {
        showDialog({
            variant: 'confirm',
            title: 'This will remove the selected record, do you want to continue?',
            onYes: () => {
                dispatch(deleteMember({ familyId: familyConfig.familyId, id: id }))
                hideDialogAndRefreshUserList()
            },
            onNo: hideDialog,
        })
    }

    const columns = [
        { name: 'firstName', title: 'First Name', getCellValue: renderLinkCell },
        { name: 'lastName', title: 'Last Name' },
        { name: 'phoneNumber', title: 'Phone No' },
        { name: 'email', title: 'Email' },
        { name: 'genderType', title: 'Gender', getCellValue: GenderTypeService.getGenderTypeData },
        { name: 'birthDate', title: 'Birth Date' },
        { name: 'updateTime', title: 'Updated Date' },
        { name: 'Action', title: 'Action', getCellValue: renderActionCell },
    ]

    const columnWidth = useMemo(
        () => [
            { columnName: 'firstName', width: min ? '120px' : '13%', wordWrapEnabled: true, minWidth: '110px' },
            { columnName: 'lastName', width: min ? '120px' : '12%', wordWrapEnabled: true, minWidth: '110px' },
            { columnName: 'phoneNumber', width: min ? '130px' : '12%', minWidth: '130px' },
            { columnName: 'email', width: min ? '190px' : '18%', wordWrapEnabled: true, minWidth: '190px' },
            { columnName: 'genderType', width: min ? '100px' : '9%', wordWrapEnabled: true, minWidth: '100px' },
            { columnName: 'birthDate', width: min ? '110px' : '11%', wordWrapEnabled: true, minWidth: '110px' },
            { columnName: 'updateTime', width: min ? '120px' : '17%', wordWrapEnabled: true, minWidth: '120px' },
            {
                columnName: 'Action',
                width: min ? '90px' : '8%',
                wordWrapEnabled: true,
                minWidth: '90px',
                align: 'center',
            },
        ],
        [min]
    )

    const [columnWidths, setColumnWidths] = useState(columnWidth)

    useEffect(() => {
        setColumnWidths(columnWidth)
    }, [min])

    const sortingColumnExtensions = [{ columnName: 'Action', sortingEnabled: false }]

    return (
        <Paper style={{ position: 'relative' }}>
            <Grid rows={members.data} columns={columns}>
                {members.totalCount !== 0 && <LoadingCircle refreshing={refreshing} />}
                <DateTimeTypeProvider for={dateTimeColumns} />
                <DateTypeProvider for={dateColumns} />
                <SortingState
                    sorting={sorting}
                    onSortingChange={onSortingChange}
                    columnSortingEnabled={true}
                    columnExtensions={sortingColumnExtensions}
                />
                <PagingState currentPage={currentPage} onCurrentPageChange={onCurrentPageChange} pageSize={pageSize} />
                <CustomPaging totalCount={members.totalCount} />
                <Table columnExtensions={columnWidths} noDataCellComponent={NoDataCellWithLoadingType} />
                <TableColumnResizing
                    columnWidths={columnWidths}
                    onColumnWidthsChange={setColumnWidths}
                    defaultColumnWidths={columnWidths}
                    resizingMode='nextColumn'
                    columnExtensions={columnWidths}
                />
                <TableColumnVisibility />
                <TableHeaderRow showSortingControls />
                <SelectionState />
                <TableSelection selectByRowClick rowComponent={TableRow} showSelectionColumn={false} />
                <PagingPanel />
            </Grid>
        </Paper>
    )
}

export default MemberGridData
